/**
 * PricingPage — Стоимость занятий
 *
 * Настройка прайс-листов по типам организаций.
 * Каждый прайс — карточка с дефолтными ценами и таблицей цен по предметам.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title, Text, Stack, Box, Group, Button, Loader, Center,
  Paper, Badge, Table, ActionIcon, Menu, NumberInput, Select,
} from '@mantine/core';
import {
  IconPlus, IconEdit, IconTrash, IconDotsVertical, IconCurrencyRubel,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import tokens from '../theme/designTokens';
import PricingFormModal from '../components/Pricing/PricingFormModal';

const ORG_TYPE_LABELS = {
  b2c: { label: 'B2C', color: 'green' },
  b2b: { label: 'B2B', color: 'blue' },
  individual: { label: 'Инд.', color: 'violet' },
  competition: { label: 'Соревнования', color: 'orange' },
};

// Форматирование цены
const formatPrice = (val) => {
  if (val == null) return '—';
  return Number(val).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });
};

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  // Состояние для добавления цены предмета (inline)
  const [addingItemFor, setAddingItemFor] = useState(null); // plan.id
  const [newItem, setNewItem] = useState({ subject_id: null, single_lesson_price: null, subscription_price: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subjectsRes] = await Promise.all([
        fetch('/api/pricing', { credentials: 'include' }),
        fetch('/api/subjects', { credentials: 'include' }),
      ]);
      if (!plansRes.ok || !subjectsRes.ok) throw new Error('Ошибка загрузки');
      setPlans(await plansRes.json());
      setSubjects(await subjectsRes.json());
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setFormModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setFormModalOpen(true);
  };

  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingPlan(null);
    loadData();
  };

  // Добавить цену предмета
  const handleAddItem = async (planId) => {
    if (!newItem.subject_id || newItem.single_lesson_price == null || newItem.subscription_price == null) {
      notifications.show({ title: 'Ошибка', message: 'Заполните все поля', color: 'red' });
      return;
    }
    try {
      const res = await fetch(`/api/pricing/${planId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newItem),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка');
      }
      setAddingItemFor(null);
      setNewItem({ subject_id: null, single_lesson_price: null, subscription_price: null });
      loadData();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    }
  };

  // Удалить цену предмета
  const handleDeleteItem = (planId, item) => {
    modals.openConfirmModal({
      title: 'Удаление цены предмета',
      centered: true,
      children: (
        <Text size="sm">
          Удалить цену для «{item.subject_name}»? Будет использована цена по умолчанию.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/pricing/${planId}/items/${item.id}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          if (!res.ok) throw new Error('Ошибка удаления');
          loadData();
        } catch (err) {
          notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
        }
      },
    });
  };

  // Предметы, ещё не добавленные в прайс
  const getAvailableSubjects = (plan) => {
    const usedIds = new Set(plan.items.map((i) => i.subject_id));
    return subjects
      .filter((s) => s.is_active && !usedIds.has(s.id))
      .map((s) => ({ value: String(s.id), label: s.name }));
  };

  if (loading) {
    return (
      <Box px="md" py="md">
        <Center py="xl"><Loader size="md" /></Center>
      </Box>
    );
  }

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Стоимость занятий
            </Title>
            <Text c="dimmed" size="sm">
              Настройка цен по типам организаций
            </Text>
          </Box>
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreatePlan}>
            Добавить прайс-лист
          </Button>
        </Group>

        {/* Прайс-листы */}
        {plans.length === 0 ? (
          <Center py="xl">
            <Text c="dimmed">Прайс-листы не созданы</Text>
          </Center>
        ) : (
          plans.map((plan) => {
            const typeConfig = ORG_TYPE_LABELS[plan.organization_type] || { label: plan.organization_type, color: 'gray' };

            return (
              <Paper key={plan.id} p="lg" radius={16} withBorder>
                <Stack gap="sm">
                  {/* Заголовок карточки */}
                  <Group justify="space-between" align="center">
                    <Group gap="sm">
                      <Badge size="lg" variant="filled" color={typeConfig.color} radius="xl">
                        {typeConfig.label}
                      </Badge>
                      <Text fw={600} size="lg">{plan.name}</Text>
                    </Group>
                    <ActionIcon variant="subtle" onClick={() => handleEditPlan(plan)}>
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Group>

                  {/* Мета-информация */}
                  <Group gap="lg">
                    <Text size="sm" c="dimmed">
                      Абонемент: <Text span fw={500} c={tokens.colors.neutral[8]}>{plan.subscription_size} занятий</Text>
                    </Text>
                    {plan.family_discount_percent > 0 && (
                      <Text size="sm" c="dimmed">
                        Скидка за {plan.family_discount_min_children}+ детей:{' '}
                        <Text span fw={500} c="green">{Number(plan.family_discount_percent)}%</Text>
                      </Text>
                    )}
                    {plan.single_lesson_default && (
                      <Text size="sm" c="dimmed">
                        По умолчанию: разовое {formatPrice(plan.single_lesson_default)}, абонемент {formatPrice(plan.subscription_default)}
                      </Text>
                    )}
                  </Group>

                  {/* Таблица цен по предметам */}
                  {plan.items.length > 0 && (
                    <Table striped highlightOnHover style={{ fontSize: tokens.typography.fontSize[13] }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Предмет</Table.Th>
                          <Table.Th>Разовое занятие</Table.Th>
                          <Table.Th>Абонемент ({plan.subscription_size} зан.)</Table.Th>
                          <Table.Th style={{ width: 40 }} />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {plan.items.map((item) => (
                          <Table.Tr key={item.id}>
                            <Table.Td>
                              <Badge size="sm" variant="filled" color="blue" radius="xl">
                                {item.subject_name}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>{formatPrice(item.single_lesson_price)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>{formatPrice(item.subscription_price)}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Menu position="bottom-end" withinPortal>
                                <Menu.Target>
                                  <ActionIcon variant="subtle" size="sm">
                                    <IconDotsVertical size={14} />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item
                                    color="red"
                                    leftSection={<IconTrash size={14} />}
                                    onClick={() => handleDeleteItem(plan.id, item)}
                                  >
                                    Удалить
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}

                  {/* Inline добавление предмета */}
                  {addingItemFor === plan.id ? (
                    <Group gap="sm" align="flex-end">
                      <Select
                        placeholder="Предмет"
                        data={getAvailableSubjects(plan)}
                        value={newItem.subject_id ? String(newItem.subject_id) : null}
                        onChange={(val) => setNewItem((prev) => ({ ...prev, subject_id: val ? Number(val) : null }))}
                        searchable
                        radius="xl"
                        style={{ minWidth: 140 }}
                      />
                      <NumberInput
                        placeholder="Разовое"
                        value={newItem.single_lesson_price}
                        onChange={(val) => setNewItem((prev) => ({ ...prev, single_lesson_price: val }))}
                        min={0}
                        radius="xl"
                        style={{ width: 120 }}
                        rightSection={<IconCurrencyRubel size={14} />}
                      />
                      <NumberInput
                        placeholder="Абонемент"
                        value={newItem.subscription_price}
                        onChange={(val) => setNewItem((prev) => ({ ...prev, subscription_price: val }))}
                        min={0}
                        radius="xl"
                        style={{ width: 120 }}
                        rightSection={<IconCurrencyRubel size={14} />}
                      />
                      <Button size="sm" onClick={() => handleAddItem(plan.id)}>Добавить</Button>
                      <Button size="sm" variant="subtle" onClick={() => { setAddingItemFor(null); setNewItem({ subject_id: null, single_lesson_price: null, subscription_price: null }); }}>
                        Отмена
                      </Button>
                    </Group>
                  ) : (
                    <Button
                      variant="subtle"
                      size="sm"
                      leftSection={<IconPlus size={14} />}
                      onClick={() => setAddingItemFor(plan.id)}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      Добавить предмет
                    </Button>
                  )}
                </Stack>
              </Paper>
            );
          })
        )}
      </Stack>

      {/* Модалка */}
      <PricingFormModal
        opened={formModalOpen}
        onClose={() => { setFormModalOpen(false); setEditingPlan(null); }}
        onSuccess={handleFormSuccess}
        plan={editingPlan}
      />
    </Box>
  );
};

export default PricingPage;
